import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--sc-radius-md)] text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 select-none active:translate-y-px",
  {
    variants: {
      variant: {
        // Amber — primary CTA zgodnie z design systemem
        default:
          "bg-[var(--sc-amber)] text-[var(--sc-text)] hover:bg-[var(--sc-amber)]/90 shadow-sm",
        secondary:
          "border border-[var(--sc-border)] bg-[var(--sc-surface)] text-[var(--sc-muted)] hover:bg-[var(--sc-bg)] shadow-sm",
        destructive:
          "border border-[#F0D0D0] bg-transparent text-[var(--sc-red)] hover:bg-[var(--sc-red-bg)]",
        ghost:
          "bg-transparent text-[var(--sc-text)] hover:bg-[var(--sc-border)]",
        link: "text-[var(--sc-blue)] underline-offset-4 hover:underline p-0 h-auto",
        dark: "bg-[var(--sc-text)] text-white hover:bg-black shadow-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm:      "h-8 rounded-[var(--sc-radius-sm)] px-3 text-xs",
        lg:      "h-11 px-5 text-base",
        icon:    "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
